import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowLeft } from "lucide-react";
import axios from "axios";
import { Wordmark, GoldRule } from "@/components/Brand";

const API = process.env.REACT_APP_BACKEND_URL;

export function StaffForgotPassword() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/api/staff/auth/request-reset`, { email: email.trim() });
            setSent(true);
            toast.success(res.data?.message || "If that email is on file, a reset link is on its way.");
        } catch (err) {
            // Even on error we show the generic success to avoid leaking existence.
            setSent(true);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-[100dvh] flex items-center justify-center px-6 py-12">
            <div className="w-full max-w-md" data-testid="staff-forgot-password-page">
                <div className="flex justify-center"><Wordmark /></div>
                <GoldRule className="mt-6" />
                <div className="mt-10 rounded-3xl glass p-8 space-y-6">
                    <div className="text-center">
                        <div className="mx-auto h-14 w-14 rounded-full glass flex items-center justify-center border-gold/50 border">
                            <Mail className="h-5 w-5 text-gold" strokeWidth={1.5} />
                        </div>
                        <h1 className="mt-6 font-serif font-light text-3xl text-white">
                            Reset your <span className="gold-text">password</span>
                        </h1>
                        <p className="mt-2 text-sm text-white/60">Enter your email and we'll send a link to set a new password. The link is valid for 30 minutes and can only be used once.</p>
                    </div>

                    {sent ? (
                        <div className="rounded-xl border border-emerald-400/40 bg-emerald-500/10 p-4 text-sm text-emerald-100" data-testid="forgot-sent-message">
                            If that email is on file, a reset link is on its way. Check your inbox — and your spam folder just in case.
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-6">
                            <label className="block">
                                <div className="text-[10px] uppercase tracking-[0.28em] text-white/70 mb-2 font-semibold">Email</div>
                                <input
                                    data-testid="forgot-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="username"
                                    required
                                    className="w-full font-ui text-base"
                                />
                            </label>
                            <button
                                type="submit"
                                disabled={submitting || !email}
                                data-testid="forgot-submit"
                                className="w-full h-14 rounded-full bg-gold text-navy font-ui font-semibold text-base tracking-wide shadow-[0_10px_30px_-8px_rgba(197,160,89,0.55)] hover:bg-gold-light active:scale-[0.98] transition-all disabled:opacity-40"
                            >
                                {submitting ? "Sending…" : "Send reset link"}
                            </button>
                        </form>
                    )}

                    <div className="text-center">
                        <Link to="/staff/login" data-testid="forgot-back-link"
                              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-gold transition-colors">
                            <ArrowLeft className="h-3 w-3" /> Back to sign in
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
